import {
  AccordionSummary,
  Accordion,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Box,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo } from 'react';
import { useFilterChannelOrEvent } from '../../../../../../redux/monitoring/hooks/useFilterChannelOrEvent';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  CommanderMonitoringFilterLogicalObject,
  getNumberOfChannels,
} from '../../../../../../redux/monitoring/api/util/filter/filter.model';
import { getPolyglot } from '../../../../../../i18n';
import { ZipDeviceControlUnit } from '../../../../../../redux/controlUnit/controlUnit.model';
import { STModal } from '../../../../../commons/Modal';
import useStateBoolean from '../../../../../../util/hooks/useStateBoolean';
import {
  CHANNELS_ANALOG_CLOUD_GENERATED,
  CLOUD_GENERATED_CU_KEY,
} from './useLabStateFilters';
import { useCloudGeneratedControls } from './useCloudGeneratedControls';

export interface CloudGeneratedFiltersModalButtonProps {
  devicesControlUnits: ZipDeviceControlUnit[];
  value: CommanderMonitoringFilterLogicalObject;
  onSave: (value: CommanderMonitoringFilterLogicalObject) => unknown;
}
export const CloudGeneratedFiltersFiltersModalButton = (
  props: CloudGeneratedFiltersModalButtonProps
) => {
  const { devicesControlUnits, value, onSave } = props;
  const polyglot = getPolyglot();
  const type = 'cloudGenerated';
  const [showConnectionModal, openConnectionModal, closeConnectionModal] =
    useStateBoolean(false);
  const controls = useCloudGeneratedControls(type, devicesControlUnits, value);
  const title = polyglot.t('lab.bandwidth_feature');

  const openModal = () => {
    controls.reset();
    openConnectionModal();
  };

  const save = () => {
    // onSave is expected to be a function that saves the filter
    onSave(controls.filterLogicalObject);
    closeConnectionModal();
  };
  const numberOfChannels = useMemo(
    () => getNumberOfChannels(value ?? {}),
    [value]
  );
  const selected = ` (${numberOfChannels.active}/${numberOfChannels.total})`;
  return (
    <>
      <Button
        onClick={openModal}
        startIcon={<FilterListIcon />}
        variant="outlined"
      >
        {title + selected}
      </Button>
      <STModal
        open={showConnectionModal}
        onClose={closeConnectionModal}
        title={title}
        fullWidth
        maxWidth="md"
        buttonActions={
          <Button onClick={save} color="primary">
            {polyglot.t('button.save')}
          </Button>
        }
      >
        <CloudGeneratedFiltersView {...props} controls={controls} type={type} />
      </STModal>
    </>
  );
};

export interface CloudGeneratedFiltersViewProps {
  devicesControlUnits: ZipDeviceControlUnit[];
  type: 'cloudGenerated';
  controls: ReturnType<typeof useFilterChannelOrEvent>;
}
export const CloudGeneratedFiltersView = (
  props: CloudGeneratedFiltersViewProps
) => {
  const { devicesControlUnits, controls } = props;
  const { toggleItem, toggleDevice, getStateItem, getStateDevice } = controls;

  return (
    <Box className="CloudGeneratedFilters" sx={{ width: '100%' }}>
      {devicesControlUnits.map((zipDeviceControlUnit) => (
        <Accordion
          key={zipDeviceControlUnit.deviceId}
          id={`device-title-${zipDeviceControlUnit.deviceId}`}
          aria-controls={`device-title-${zipDeviceControlUnit.deviceId}`}
          className={`CloudGeneratedFilters__Device Device-${zipDeviceControlUnit.deviceId}`}
          defaultExpanded={true}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Checkbox
              id={`device-checkbox-${zipDeviceControlUnit.deviceId}`}
              inputProps={
                {
                  'data-testid': `device-checkbox-${zipDeviceControlUnit.deviceId}`,
                } as any
              }
              edge="start"
              checked={getStateDevice(zipDeviceControlUnit) === 'checked'}
              onClick={(e) => {
                e.stopPropagation();
                toggleDevice(zipDeviceControlUnit);
              }}
              indeterminate={
                getStateDevice(zipDeviceControlUnit) === 'indeterminate'
              }
              tabIndex={-1}
            />
            <Typography variant="h5" display="flex" alignItems="center">
              {zipDeviceControlUnit.deviceName}
            </Typography>
          </AccordionSummary>
          <Box sx={{ padding: 1 }}>
            <List>
              {CHANNELS_ANALOG_CLOUD_GENERATED.map((eventId) => {
                const primaryText = eventId;

                return (
                  <ListItem
                    key={eventId}
                    className={`CloudGeneratedFilters__Event Event-${eventId}`}
                    disablePadding
                  >
                    <ListItemButton
                      role={undefined}
                      onClick={() =>
                        toggleItem(
                          zipDeviceControlUnit.deviceId,
                          CLOUD_GENERATED_CU_KEY,
                          eventId
                        )
                      }
                      dense
                    >
                      <ListItemIcon>
                        <Checkbox
                          id={`event-checkbox-${zipDeviceControlUnit.deviceId}-${CLOUD_GENERATED_CU_KEY}-${eventId}`}
                          edge="start"
                          checked={
                            getStateItem(
                              zipDeviceControlUnit.deviceId,
                              CLOUD_GENERATED_CU_KEY,
                              eventId
                            ) === 'checked'
                          }
                          indeterminate={
                            getStateItem(
                              zipDeviceControlUnit.deviceId,
                              CLOUD_GENERATED_CU_KEY,
                              eventId
                            ) === 'indeterminate'
                          }
                          tabIndex={-1}
                          disableRipple
                          inputProps={
                            {
                              'aria-labelledby': `event-title-${zipDeviceControlUnit.deviceId}-${CLOUD_GENERATED_CU_KEY}-${eventId}-label`,
                              'data-testid': `event-checkbox-${zipDeviceControlUnit.deviceId}-${CLOUD_GENERATED_CU_KEY}-${eventId}`,
                            } as any
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={`event-title-${zipDeviceControlUnit.deviceId}-${CLOUD_GENERATED_CU_KEY}-${eventId}-label`}
                        primary={primaryText}
                        primaryTypographyProps={{
                          sx: { wordBreak: 'break-all' },
                        }}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-all' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Accordion>
      ))}
    </Box>
  );
};

export default CloudGeneratedFiltersView;
